import { Button } from "@mui/material";
import Modal from "../Modal";
import { IMaskInput } from "react-imask";
import Loader from "../Loader";
import { useEffect, useRef, useState } from "react";
import { sendPatchRequest, sendPostRequest } from "../../utils";

const EmployeeForm = ({
  data,
  open,
  onClose,
  doFetchEmployees,
  setSuccess,
}) => {
  const phoneRef = useRef(null);

  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [form, setForm] = useState({
    photo: null,
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    username: "",
    password: "",
  });

  const onInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setIsLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("photo", form.photo);
      formData.append("first_name", form.firstName);
      formData.append("last_name", form.lastName);
      formData.append("phone_number", form.phone);
      formData.append("email", form.email);
      formData.append("username", form.username);
      formData.append("password", form.password);

      if (data) {
        // patch request
        const response = await sendPatchRequest(
          `employees/${data.id}`,
          formData,
          true
        );

        if (response.ok) {
          doFetchEmployees();
          setSuccess("Employee updated successfully.");
          onClose();
        } else {
          const { message } = await response.json();
          setError(message || "Failed to update employee.");
        }
      } else {
        const response = await sendPostRequest("employees", formData, true);
        if (response.ok) {
          doFetchEmployees();
          setSuccess("Employee created successfully.");
          onClose();
        } else {
          const { message } = await response.json();
          setError(message || "Failed to create employee.");
        }
      }
    } catch (error) {
      console.log(error);
      setError("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files[0];

    setForm((prev) => ({ ...prev, [event.target.name]: file }));

    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const isValid = /^\+1 \(\d{3}\) \d{3}-\d{4}$/.test(form.phone);
  const handleBlur = () => {
    if (!isValid) {
      onInputChange({ target: { name: "phone", value: "" } });
    }
  };

  useEffect(() => {
    setError(null);

    if (open) {
      setForm({
        photo: data?.photo || "",
        firstName: data?.first_name || "",
        lastName: data?.last_name || "",
        phone: data?.phone_number || "",
        email: data?.email || "",
        username: data?.username || "",
        password: data?.password || "",
      });

      if (data?.phone_number) {
        phoneRef.current?.input?.unmask();
      }
    } else {
      // Reset form and phone input when modal closes
      setForm({
        photo: null,
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
        username: "",
        password: "",
      });
      setPhotoPreview(null);
      if (phoneRef.current?.input) {
        phoneRef.current.input.value = "";
      }
    }
  }, [open, data]);

  return (
    <Modal
      open={open}
      handleClose={onClose}
      style={{ width: "620px", backgroundColor: "#f5f5f5" }}
    >
      <div className="flex flex-col gap-4">
        <h1 className="text-xl font-bold">
          {data ? "Update Employee" : "Create Employee"}
        </h1>
        <div className="space-y-2">
          {error && (
            <div className="text-red-500 bg-red-50 border-red-200 border text-base px-2 py-1 mb-4">
              {error}
            </div>
          )}
          <form
            onSubmit={handleSubmit}
            className="space-y-4"
            encType="multipart/form-data"
          >
            <div className="flex flex-col items-center gap-4">
              <img
                src={
                  photoPreview ||
                  (form.photo
                    ? `${process.env.REACT_APP_DOMAIN}/uploads/employees/${form.photo}`
                    : "/default-avatar.png")
                }
                alt=""
                className="w-32 h-32 rounded-full object-cover border-4 border-gray-300"
              />
              <Button
                component="label"
                role={undefined}
                variant="outlined"
                tabIndex={-1}
                className="w-56"
              >
                Upload Photo
                <input
                  type="file"
                  name="photo"
                  onChange={handlePhotoChange}
                  className="hidden"
                  accept="image/*"
                />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-semibold text-gray-800">
                  First Name
                </label>
                <input
                  value={form.firstName}
                  type="text"
                  name="firstName"
                  onChange={onInputChange}
                  required
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder=""
                  disabled={isLoading}
                />
              </div>
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-semibold text-gray-800">
                  Last Name
                </label>
                <input
                  value={form.lastName}
                  type="text"
                  name="lastName"
                  onChange={onInputChange}
                  required
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder=""
                  disabled={isLoading}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-semibold text-gray-800">
                  Phone Number
                </label>
                <IMaskInput
                  name="phone"
                  type="tel"
                  mask="+1 (000) 000-0000"
                  value={form.phone}
                  ref={phoneRef}
                  unmask={false}
                  onAccept={(val) =>
                    onInputChange({ target: { name: "phone", value: val } })
                  }
                  onBlur={handleBlur}
                  placeholder="+1 (123) 456-7890"
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-semibold text-gray-800">
                  Email Address
                </label>
                <input
                  value={form.email}
                  type="email"
                  name="email"
                  onChange={onInputChange}
                  required
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder=""
                  disabled={isLoading}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-semibold text-gray-800">
                  Username
                </label>
                <input
                  value={form.username}
                  type="text"
                  name="username"
                  onChange={onInputChange}
                  required
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder=""
                  disabled={isLoading}
                />
              </div>
              <div className="flex flex-col relative">
                <label className="mb-2 text-sm font-semibold text-gray-800">
                  Password
                </label>
                <input
                  value={form.password}
                  type={showPassword ? "text" : "password"}
                  name="password"
                  onChange={onInputChange}
                  required={!data}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder=""
                  disabled={isLoading}
                />
                <span
                  onClick={() => setShowPassword(!showPassword)}
                  className="cursor-pointer absolute right-3 bottom-2"
                >
                  {showPassword ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="size-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="size-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                      />
                    </svg>
                  )}
                </span>
              </div>
            </div>
            <div>
              <div className="flex gap-2 mt-10">
                <Button
                  type="submit"
                  disabled={isLoading}
                  variant="contained"
                  className="w-1/2"
                >
                  <span>{data ? "Update" : "Create"}</span>
                  {isLoading ? <Loader /> : ""}
                </Button>
                <Button
                  type="button"
                  disabled={isLoading}
                  variant="outlined"
                  className="w-1/2"
                  onClick={onClose}
                >
                  <span>Cancel</span>
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default EmployeeForm;
